import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { formatDateTime, cn } from '../../lib/utils';
import { 
  BookOpen, Image, Calendar, Heart, Tag, User
} from 'lucide-react';
import { toast } from 'sonner';

const FamilyDashboard = () => {
  const { api, user } = useAuth();
  const [stats, setStats] = useState({});
  const [participants, setParticipants] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState('');
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedParticipant) {
      fetchDiary();
    }
  }, [selectedParticipant]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, participantsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/participants')
      ]);
      setStats(statsRes.data);
      setParticipants(participantsRes.data);
      
      if (participantsRes.data.length > 0) {
        setSelectedParticipant(participantsRes.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDiary = async () => {
    try {
      const res = await api.get('/diary', { params: { participant_id: selectedParticipant } });
      setRecentPosts(res.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch diary');
    }
  };

  const currentParticipant = participants.find(p => p.id === selectedParticipant);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="family-dashboard">
      <PageHeader 
        title="Family Dashboard"
        description="Stay connected with your loved one's daily activities"
      />

      {/* Participant Selector */}
      {participants.length > 1 && (
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <User className="h-5 w-5 text-muted-foreground" />
              <Select value={selectedParticipant} onValueChange={setSelectedParticipant}>
                <SelectTrigger className="w-64" data-testid="participant-selector">
                  <SelectValue placeholder="Select participant" />
                </SelectTrigger>
                <SelectContent>
                  {participants.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.first_name} {p.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participant Info */}
      {currentParticipant && (
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardContent className="py-6">
            <div className="flex items-center gap-6">
              <Avatar className="h-20 w-20">
                <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                  {currentParticipant.first_name[0]}{currentParticipant.last_name[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-2xl font-bold">
                  {currentParticipant.first_name} {currentParticipant.last_name}
                </h2>
                <p className="text-muted-foreground">{currentParticipant.house_name}</p>
                {currentParticipant.goals?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {currentParticipant.goals.slice(0, 2).map((goal, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {goal.substring(0, 30)}...
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Linked Participants"
          value={stats.linked_participants || 0}
          icon={User}
        />
        <StatCard
          title="Recent Diary Posts"
          value={stats.recent_diary_posts || 0}
          icon={BookOpen}
        />
        <StatCard
          title="This Week's Updates"
          value={recentPosts.length}
          icon={Calendar}
        />
      </div>

      {/* Recent Diary Posts */}
      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Heart className="h-5 w-5 text-pink-500" />
            Recent Updates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {recentPosts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Image className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No diary posts yet</p>
            </div>
          ) : (
            recentPosts.map(post => (
              <div 
                key={post.id} 
                className="p-4 rounded-lg border border-border/40 bg-background/50"
                data-testid={`diary-post-${post.id}`}
              >
                <div className="flex items-start gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {post.author_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{post.author_name}</p>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(post.created_at)}
                      </span>
                    </div>
                    <p className="text-sm mt-2 text-foreground">{post.text}</p>
                    {post.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {post.tags.map(tag => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            <Tag className="h-3 w-3 mr-1" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {post.photos?.length > 0 && (
                      <div className="flex gap-2 mt-3">
                        {post.photos.map((url, idx) => (
                          <img 
                            key={idx}
                            src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                            alt=""
                            className="h-24 w-24 object-cover rounded-lg"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FamilyDashboard;
